import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Row,
  Segmented,
  Slider,
  Space,
  Switch,
  Typography,
  Upload,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { LookupBox } from './lookup-tab';
import { RevealMode, revealImage } from './reveal';

/** Ảnh lớn hơn thì thu nhỏ trước khi xử lý (mã ẩn là chữ to nên vẫn còn). */
const MAX_SIDE = 4096;

const MODES: { value: RevealMode; label: string }[] = [
  { value: 'local', label: 'Khử nền (khuyên dùng)' },
  { value: 'bright', label: 'Kéo vùng sáng' },
  { value: 'dark', label: 'Kéo vùng tối' },
];

// Thử nghiệm (docs/watermark/README.md): lọc thông cao ×30–40 đọc được alpha ~4/255.
const DEFAULTS = { local: 32, bright: 20, dark: 20 };

/** Soi lớp mã ẩn trên ảnh chụp màn hình — xử lý ngay trên máy, không tải ảnh lên server. */
const RevealTab = () => {
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [mode, setMode] = useState<RevealMode>('local');
  const [gain, setGain] = useState(DEFAULTS.local);
  const [radius, setRadius] = useState(8);
  const [invert, setInvert] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hasImage, setHasImage] = useState(false);
  // Tăng mỗi lần nạp ảnh — nạp lại ảnh trùng tên vẫn xử lý lại.
  const [imageVersion, setImageVersion] = useState(0);
  const sourceRef = useRef<ImageData | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const loadFile = useCallback((file: File | Blob, name: string) => {
    if (!file.type.startsWith('image/')) {
      setError('Chỉ nhận file ảnh (PNG, JPG, WEBP…).');
      return;
    }
    setError('');
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
      const w = Math.round(img.naturalWidth * scale);
      const h = Math.round(img.naturalHeight * scale);
      const work = document.createElement('canvas');
      work.width = w;
      work.height = h;
      const ctx = work.getContext('2d', {
        willReadFrequently: true,
      } as any) as CanvasRenderingContext2D | null;
      if (!ctx) {
        setError('Trình duyệt không hỗ trợ xử lý ảnh.');
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      sourceRef.current = ctx.getImageData(0, 0, w, h);
      URL.revokeObjectURL(url);
      setFileName(name);
      setHasImage(true);
      setImageVersion((version) => version + 1);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      setError('Không đọc được ảnh này.');
    };
    img.src = url;
  }, []);

  // Dán ảnh (Ctrl+V) ở bất kỳ đâu trên tab.
  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const item = Array.from(event.clipboardData?.items ?? []).find((entry) =>
        entry.type.startsWith('image/')
      );
      const file = item?.getAsFile();
      if (file) loadFile(file, 'Ảnh dán từ bộ nhớ tạm');
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [loadFile]);

  // Xử lý lại khi đổi thông số (trễ một nhịp để kéo thanh trượt không giật).
  useEffect(() => {
    const source = sourceRef.current;
    const canvas = canvasRef.current;
    if (!hasImage || !source || !canvas) return undefined;
    setBusy(true);
    const timer = window.setTimeout(() => {
      canvas.width = source.width;
      canvas.height = source.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.putImageData(
          showOriginal ? source : revealImage(source, { mode, gain, radius, invert }),
          0,
          0
        );
      }
      setBusy(false);
    }, 120);
    return () => window.clearTimeout(timer);
  }, [hasImage, imageVersion, mode, gain, radius, invert, showOriginal]);

  const openFullSize = () => {
    canvasRef.current?.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }, 'image/png');
  };

  return (
    <Row gutter={[24, 24]}>
      <Col xs={24} xl={8}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Card>
            <Upload.Dragger
              accept="image/*"
              showUploadList={false}
              beforeUpload={(file) => {
                loadFile(file, file.name);
                return false;
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Kéo ảnh bị lộ vào đây, bấm để chọn, hoặc Ctrl+V để dán
              </p>
              <p className="ant-upload-hint">Ảnh chỉ xử lý trên máy này, không gửi lên máy chủ.</p>
            </Upload.Dragger>
            {fileName ? (
              <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                Đang soi: {fileName}
              </Typography.Text>
            ) : null}
          </Card>
          {error ? <Alert type="error" showIcon message={error} /> : null}
          <Card title="Chỉnh độ soi">
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Segmented
                block
                options={MODES}
                value={mode}
                onChange={(value) => {
                  const next = value as RevealMode;
                  setMode(next);
                  setGain(DEFAULTS[next]);
                }}
              />
              <div>
                <Typography.Text>Khuếch đại: {gain}×</Typography.Text>
                <Slider min={4} max={60} value={gain} onChange={setGain} />
              </div>
              {mode === 'local' ? (
                <div>
                  <Typography.Text>Bán kính nền: {radius}px</Typography.Text>
                  <Slider min={3} max={24} value={radius} onChange={setRadius} />
                </div>
              ) : null}
              <Space wrap>
                <Switch checked={invert} onChange={setInvert} /> <span>Đảo màu</span>
                <Switch checked={showOriginal} onChange={setShowOriginal} />{' '}
                <span>Xem ảnh gốc</span>
              </Space>
            </Space>
          </Card>
          <Card size="small">
            <Typography.Paragraph style={{ margin: 0 }} type="secondary">
              Chữ watermark rất mờ (vài %) nên khó đọc bằng mắt: công cụ này làm rõ các dòng chữ
              nghiêng lên phải, dòng cuối có mã dạng <Typography.Text code>3F7-K2M</Typography.Text>{' '}
              kèm ngày giờ. Chưa thấy thì tăng khuếch đại, thử "Kéo vùng sáng" cho nền trắng. Nếu bật
              lớp mã thứ hai thì còn thấy mã chữ to nghiêng xuống (ảnh nén qua Zalo/Messenger thường
              mất lớp này).
            </Typography.Paragraph>
          </Card>
        </Space>
      </Col>
      <Col xs={24} xl={16}>
        <Card
          title={busy ? 'Đang xử lý…' : 'Kết quả'}
          extra={
            hasImage ? (
              <Button size="small" onClick={openFullSize}>
                Mở cỡ thật
              </Button>
            ) : null
          }
        >
          {hasImage ? (
            <div
              style={{
                maxHeight: '70vh',
                overflow: 'auto',
                background: '#808080',
                borderRadius: 8,
              }}
            >
              <canvas
                ref={canvasRef}
                style={{ display: 'block', maxWidth: '100%', margin: '0 auto' }}
              />
            </div>
          ) : (
            <Typography.Text type="secondary">Chưa có ảnh.</Typography.Text>
          )}
          <div style={{ marginTop: 16 }}>
            <LookupBox />
          </div>
        </Card>
      </Col>
    </Row>
  );
};

export default RevealTab;
